import { PieChart, Pie, ResponsiveContainer, Tooltip, Cell, Legend } from 'recharts'
import { Box, Button, Callout, Card, Flex, Text, TextField } from '@radix-ui/themes'
import { PropsWithChildren, ReactNode, useEffect, useMemo, useState } from 'react'
import DataTable, { TableColumn } from 'react-data-table-component'
import { accentColors } from '@radix-ui/themes/props'
import { format, isValid, parse } from 'date-fns'
import * as XLSX from 'xlsx'
import {
  CalendarIcon,
  CircleIcon,
  FileSpreadsheetIcon,
  MinusCircleIcon,
  PlusCircleIcon,
  SearchIcon,
} from 'lucide-react'

import { dateFullToday, dateToday, formatDateToYYYY_MM_DD, formatWithThousandSep, shuffleWithSeed } from '@/utils/utils'
import { ScreenWrapper } from '@/modules/common/components/ScreenWrapper'
import { useSalesTotalFetcher } from '@/hooks/useSalesTotalFetcher'
import { Rule, validateFields } from '@/utils/validateForm'
import SalesTotal from '@/interfaces/SalesTotal'

interface ChartData {
  label: string
  value: number
}

interface BarChartComponentProps {
  data: ChartData[]
}

interface DateRange {
  since: string
  until: string
}

interface ReportCalloutProps extends Pick<Callout.RootProps, 'color'>, PropsWithChildren {
  icon: ReactNode
  label: string
}

const ReportCallout = ({ color, icon, children, label }: ReportCalloutProps) => (
  <Callout.Root color={color}>
    <Callout.Icon>{icon}</Callout.Icon>
    <Callout.Text align="right">
      {children}
      {label && (
        <>
          <br />
          <Text size="1">{label}</Text>
        </>
      )}
    </Callout.Text>
  </Callout.Root>
)

const COLORS = shuffleWithSeed(accentColors, 234798).map((c) => `var(--${c}-10)`)

const getCellColor = (index: number) => (COLORS.length ? COLORS[index % COLORS.length] : 'var(--gray-10)')

const MarginBarChart = ({ data }: BarChartComponentProps) => {
  const chartData = data.map((item) => ({
    value: Math.abs(item.value),
    name: item.label,
  }))

  return (
    <Box width="100%" height="400px">
      <ResponsiveContainer>
        <PieChart>
          <Pie
            style={{ outline: 'none' }}
            innerRadius="50%"
            outerRadius="80%"
            data={chartData}
            paddingAngle={1}
            dataKey="value"
            nameKey="name"
          >
            {chartData.map((_, index) => (
              <Cell key={`cell-${index}`} fill={getCellColor(index)} />
            ))}
          </Pie>
          <Tooltip formatter={(value, label) => [`$${formatWithThousandSep(Number(value))}`, label]} />
          <Legend fontStyle="bold" />
        </PieChart>
      </ResponsiveContainer>
    </Box>
  )
}

const marginSort = (rowA: SalesTotal, rowB: SalesTotal) => rowA.margin - rowB.margin

const columns: TableColumn<SalesTotal>[] = [
  {
    name: 'Vendedor',
    selector: (row) => `${row.firstname} ${row.lastname}`,
    sortable: true,
    grow: 4,
  },
  {
    name: <div>Negocios Asignados</div>,
    selector: (row) => row.totalDeals,
    sortable: true,
    right: true,
    grow: 1,
  },
  {
    name: <div>Negocios Cancelados</div>,
    selector: (row) => row.totalCancelledDeals,
    sortable: true,
    right: true,
    grow: 2,
  },
  {
    name: <div>Negocios Gestionados</div>,
    selector: (row) => row.dealsWithComments,
    sortable: true,
    right: true,
    grow: 1,
  },
  {
    name: 'Cotizaciones',
    selector: (row) => row.totalEstimates,
    sortable: true,
    right: true,
    grow: 2,
  },
  {
    name: 'Reservas',
    selector: (row) => row.totalReservations,
    sortable: true,
    right: true,
  },
  {
    name: 'N° Ventas',
    selector: (row: SalesTotal) => row.totalSaleNotes,
    sortable: true,
    right: true,
  },
  {
    name: 'Seguros',
    selector: (row: SalesTotal) => row.totalInsuranceCount,
    sortable: true,
    right: true,
  },
  {
    name: 'Créditos',
    selector: (row: SalesTotal) => row.totalCreditCount,
    sortable: true,
    right: true,
  },
  {
    name: 'TAG',
    selector: (row: SalesTotal) => row.totalTagCount,
    sortable: true,
    right: true,
  },
  {
    name: <div>Autos en parte de pago</div>,
    selector: (row: SalesTotal) => row.totalTradeInCount,
    sortable: true,
    right: true,
    grow: 2,
  },
  {
    name: <div>% Penetración créditos</div>,
    selector: (row: SalesTotal) =>
      (row.totalCreditPercentage ?? 0).toLocaleString(navigator.language, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    sortable: true,
    right: true,
    grow: 2,
  },
  {
    name: <div>Monto Total Margen</div>,
    selector: (row: SalesTotal) => formatWithThousandSep(row.margin),
    sortable: true,
    right: true,
    grow: 3,
    sortFunction: marginSort,
  },
]

const paginationComponentOptions = {
  rowsPerPageText: 'Filas por página',
  rangeSeparatorText: 'de',
  selectAllRowsItem: true,
  selectAllRowsItemText: 'Todos',
}

const today = new Date()
const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)

const formatDate = (dateStr: string) => {
  const date = parse(dateStr, 'yyyy-MM-dd', new Date())

  if (!isValid(date)) return ''

  return format(date, 'PP')
}

const getCalloutColor = (margin: number): Callout.RootProps['color'] => {
  if (margin < 0) {
    return 'red'
  }

  if (margin > 0) {
    return 'green'
  }

  return 'gray'
}

const getCalloutIcon = (margin: number) => {
  if (margin < 0) {
    return <MinusCircleIcon />
  }

  if (margin > 0) {
    return <PlusCircleIcon />
  }

  return <CircleIcon />
}

export const SalesReport = () => {
  const { salesTotals, errorSalesTotal, loading, fetchSalesTotals } = useSalesTotalFetcher()
  const [formData, setFormData] = useState<DateRange>({
    since: formatDateToYYYY_MM_DD(firstDay),
    until: dateToday(),
  })

  const [dateRange, setDateRange] = useState<DateRange>({ ...formData })

  // Alphabetically sort by full name for consistent default ordering across table, chart, and export
  const sortedSalesTotals = useMemo(() => {
    const toName = ({ firstname, lastname }: SalesTotal) => `${firstname ?? ''} ${lastname ?? ''}`.trim()

    return [...salesTotals].sort((a, b) =>
      toName(a).localeCompare(toName(b), undefined, {
        sensitivity: 'base',
      })
    )
  }, [salesTotals])

  useEffect(() => {
    setDateRange({ ...formData })
  }, [salesTotals, formData])

  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  useEffect(() => {
    void fetchSalesTotals(formData)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps -- Initial fetch on mount

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target

    if (!formData) {
      return
    }

    const nextFormData = { ...formData, [name]: value }

    const fieldsToValidate = [
      { name: 'since', value: nextFormData.since, rule: Rule.REQUIRED },
      { name: 'until', value: nextFormData.until, rule: Rule.REQUIRED },
    ]

    const validationErrors = validateFields(fieldsToValidate)

    setErrors(validationErrors)
    setFormData(nextFormData)
  }

  const mapDataForExport = (data: SalesTotal[]) =>
    data.map((row) => ({
      // FIXME: Use translation function for labels
      ['Vendedor']: `${row.firstname} ${row.lastname}`,
      ['Negocios Asignados']: row.totalDeals,
      ['Negocios Cancelados']: row.totalCancelledDeals,
      ['Negocios Gestionados']: row.dealsWithComments,
      ['Cotizaciones']: row.totalEstimates,
      ['Reservas']: row.totalReservations,
      ['N° Ventas']: row.totalSaleNotes,
      ['Seguros']: row.totalInsuranceCount,
      ['Créditos']: row.totalCreditCount,
      ['TAG']: row.totalTagCount,
      ['Autos en parte de pago']: row.totalTradeInCount,
      ['% Penetración créditos']: Number((row.totalCreditPercentage ?? 0).toFixed(2)),
      ['Monto Total Margen']: row.margin,
    }))

  const exportToExcel = () => {
    if (!sortedSalesTotals || sortedSalesTotals.length === 0) {
      return
    }

    const mappedData = mapDataForExport(sortedSalesTotals)
    const workbook = XLSX.utils.book_new()

    const worksheet = XLSX.utils.json_to_sheet(mappedData)

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Ventas')

    const name = `SalesTotal-report-${dateFullToday()}.xlsx`

    XLSX.writeFile(workbook, name)
  }

  const nameTotalList = useMemo(
    () =>
      sortedSalesTotals.map((item) => ({
        label: `${item.firstname} ${item.lastname}`,
        value: item.margin,
      })),
    [sortedSalesTotals]
  )

  const margin = salesTotals.reduce((sum, m) => sum + Number(m.margin ?? 0), 0)

  return (
    <ScreenWrapper>
      {errorSalesTotal?.message && <Text color="red">{errorSalesTotal.message}</Text>}
      <Card mt="2">
        <Flex gap="4" align="center" justify="between">
          <Flex gap="2">
            <Flex gap="2" align="center">
              <Text as="label">Desde</Text>
              <TextField.Root
                onChange={handleInputChange}
                value={formData.since}
                disabled={loading}
                name="since"
                type="date"
              />
            </Flex>
            {errors.since && (
              <Text color="red" size="1">
                {errors.since}
              </Text>
            )}

            <Flex gap="2" align="center">
              <Text as="label">Hasta</Text>
              <TextField.Root
                onChange={handleInputChange}
                value={formData.until}
                disabled={loading}
                name="until"
                type="date"
              />
            </Flex>
            {errors.until && (
              <Text color="red" size="1">
                {errors.until}
              </Text>
            )}
            <Button onClick={() => fetchSalesTotals(formData)} disabled={loading} loading={loading}>
              <SearchIcon />
              <Text>Buscar</Text>
            </Button>
          </Flex>

          <Button onClick={exportToExcel} disabled={loading || salesTotals.length === 0} color="green">
            <FileSpreadsheetIcon />
            <Text>Exportar</Text>
          </Button>
        </Flex>
      </Card>

      {sortedSalesTotals.length > 0 && (
        <>
          <Flex gap="2" wrap="wrap">
            <ReportCallout label="Período" color="gray" icon={<CalendarIcon />}>
              <Text weight="bold">{formatDate(dateRange.since)}</Text>
              {' a '}
              <Text size="2" weight="bold">
                {formatDate(dateRange.until)}
              </Text>
            </ReportCallout>

            <ReportCallout label="Margen Total" color={getCalloutColor(margin)} icon={getCalloutIcon(margin)}>
              <Text weight="bold">${formatWithThousandSep(margin)}</Text>
            </ReportCallout>
          </Flex>

          <Card>
            <MarginBarChart data={nameTotalList} />
          </Card>

          <Flex direction="column" mb="2">
            <Card>
              <DataTable
                paginationComponentOptions={paginationComponentOptions}
                noDataComponent="[No hay datos]"
                data={sortedSalesTotals}
                columns={columns}
                pagination
                striped
                dense
              />
            </Card>
          </Flex>
        </>
      )}
    </ScreenWrapper>
  )
}
